// src/components/ModalLayout1.jsx
import React, { useRef, useState } from "react";
import classNames from "classnames";
import { XIcon, DownloadIcon, ArrowsExpandIcon } from "@heroicons/react/solid";
import PrintButton from "./PrintButton";
import ReceiptPrint from "./ReceiptPrint";
import Table, { SelectColumnFilter } from "./Table";
import Spinner from "../loaders/Spinner";
import TransformValue from "./TransformValue";
import { appendQueryToUri } from "../util/generalUtil";
import appFetch from "../appFetch";
import apiEndpoints from "../apiEndpoints";
import moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import PrintPreview from "./PrintPreview";
import { useTheme } from "styled-components";
import CloseIcon from "../icons/CloseIcon";
import ReceiptPrintButton from "./ReceiptPrintButton";

const MySwal = withReactContent(
  Swal.mixin({
    customClass: {
      confirmButton:
        "text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2 bg-primary",
      cancelButton:
        "text-white font-medium rounded-lg text-sm px-5 py-2.5 text-center mr-2 mb-2 bg-primary",
    },
    buttonsStyling: false,
  })
);

const DataRow = ({ itemKeys, drillable, line }) => {
  const [drill, showDrill] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const showDetail = (item) => {
    console.log("the line is", item);
    setLoading(true);

    let postData = {
      layoutId: item?.layoutid,
      targetId: item?.targetid,
      target: item?.target,
      drillId: item.drillid?.toString(),
      targetName: item?.target_name,
      targetType: item?.target_type,
    };

    appFetch
      .get(appendQueryToUri(apiEndpoints.menuView, postData))
      .then((res) => {
        let options = (res.options ?? [])[0] ?? null;
        showDrill(options);
      })
      .catch((e) => console.log(e.message))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <tr class="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
        {itemKeys?.map((str, idx) => (
          <td class="px-4 py-4">
            <TransformValue value={line[str]} />
          </td>
        ))}
        {drillable && (
          <td class="px-4 py-4">
            <button
              className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-background-yellow shadow hover:text-main-black"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (drill) {
                  showDrill(null);
                  return;
                }
                showDetail(line);
              }}
            >
              {drill ? (
                <CloseIcon fill={theme?.colors?.primary} width={10} />
              ) : (
                <ArrowsExpandIcon fill={theme?.colors?.primary} width={18} />
              )}
            </button>
          </td>
        )}
      </tr>
      {(loading || drill) && (
        <tr>
          <td colSpan={++itemKeys.length}>
            {loading && (
              <div className="grid place-items-center py-6">
                <Spinner fill="#e2e3e4" width={42} height={42} />
              </div>
            )}

            {drill?.gridtype === "horizontal" &&
              (() => {
                let drillKeys = Object.keys(
                  (drill?.grid ?? [])[0] ?? {}
                ).filter(
                  (head) =>
                    ![
                      "drillable",
                      "target_name",
                      "target_type",
                      "drillid",
                      "targetid",
                    ].includes(head)
                );

                return (
                  <div class="p-6 bg-gray-100 w-full">
                    <div class="relative overflow-x-auto shadow-md sm:rounded-lg">
                      <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                        <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                          <tr>
                            {drillKeys?.map((str, idx) => (
                              <th
                                scope="col"
                                class="px-4 py-3 text-xs"
                                key={idx}
                              >
                                {str}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {drill?.grid?.map((line, key) => {
                            return (
                              <tr class="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                                {drillKeys?.map((str, idx) => (
                                  <td class="px-4 py-4 text-xs">
                                    <TransformValue value={line[str]} />
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                          {drill?.grid?.length === 0 && (
                            <tr class="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                              <td class="px-4 py-4 text-xs">
                                <span className="flex justify-center">
                                  No record available.
                                </span>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

            {drill?.gridtype === "vertical" &&
              (() => {
                const objKeys = Object.keys(
                  (drill?.grid ?? [])[0] ?? {}
                ).filter((key) => !excludeKeys.includes(key));
                return (
                  <div class="p-6 w-full">
                    <div class="relative overflow-x-auto shadow-md sm:rounded-lg">
                      <ul className="modal-list">
                        {drill?.grid.map((record) => (
                          <li className="modal-list-item list-none">
                            <ul className="grid gap-1 my-6 border-b border-gray-200 mb-3">
                              {objKeys.map((str, idx) => {
                                return (
                                  <li
                                    className={classNames(
                                      "flex justify-between"
                                    )}
                                    key={idx}
                                  >
                                    <div
                                      className={classNames("py-2 px-3 w-36", {
                                        "bg-gray-200": (idx + 1) % 2 != 0,
                                      })}
                                    >
                                      <span className="text-gray-700 font-semibold text-xs">
                                        {str}
                                      </span>
                                    </div>
                                    <div
                                      className={classNames(
                                        "ml-0.5 py-2 px-3 flex-grow",
                                        { "bg-gray-200": (idx + 1) % 2 != 0 }
                                      )}
                                    >
                                      <span className="text-xs">
                                        <TransformValue value={record[str]} />
                                      </span>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })()}
          </td>
        </tr>
      )}
    </>
  );
};

const excludeKeys = [
  "drillable",
  "drillid",
  "target_name",
  "target_type",
  "targetid",
];

export default function ModalLayout1({
  options: optionsProp,
  targetid,
  layoutid,
  drillid,
  target,
  targetType,
  setLoading,
  handleCloseModal,
  dateFilter,
  accessFilter,
  targetName,
}) {
  const options = (optionsProp ?? [])[0] ?? {};
  const itemKeys = Object.keys((options?.grid ?? [])[0] ?? {}).filter(
    (key) => !excludeKeys.includes(key)
  );
  const drillable = (options?.grid ?? [])[0]?.drillable === "YES";
  const [valuee, setValue] = useState(options?.grid ?? []);
  const [dateRange, setDateRange] = useState(null);
  const searchRef = useRef(null);

  const columns = itemKeys?.map((name) =>
    name == "Copde"
      ? {
          Header: name,
          accessor: name,
          Filter: SelectColumnFilter,
          filter: "includes",
        }
      : {
          Header: name,
          accessor: name,
        }
  );

  const printPaper = () => {
    console.log("printing...");
  };

  const exportData = () => {
    const searchText = searchRef.current.value;
    setLoading(true);
    let postData = {
      layoutId: layoutid,
      targetId: targetid,
      target: target,
      drillId: drillid,
      targetType: targetType,
      outputType: "DATASET",
      targetName: targetName,
      searchText: searchText,
      //   dateFilter: dateRange ? JSON.stringify(dateRange) : null,
      dateFilter: dateRange != null ? JSON.stringify(dateRange) : dateFilter,
      accessFilter: accessFilter ?? null,
    };

    let token = localStorage.getItem("token") ?? "";
    fetch(appendQueryToUri(apiEndpoints.downloadData, postData), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok)
          throw new Error("Something went wrong! could not download file.");
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${options?.Title?.replace(
          /\s+/,
          ""
        )?.toLowerCase()}-${moment().format("YYYY-MM-DD_h:msa")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((e) => {
        MySwal.fire({
          text: e.message,
          icon: "error",
        });
      })
      .finally((e) => setLoading(false));
  };

  const filterByDateRange = (dateRange) => {
    setLoading(true);
    let postData = {
      layoutId: layoutid,
      targetId: targetid,
      target: "modal",
      dateFilter: dateRange != null ? JSON.stringify(dateRange) : dateFilter,
      accessFilter: accessFilter,
    };
    appFetch
      .get(appendQueryToUri(apiEndpoints.menuView, postData))
      .then((res) => {
        setDateRange(dateRange);
        let opt = (res.options ?? [])[0] ?? null;
        setValue(opt?.grid ?? []);
      })
      .catch((e) => console.log(e.message))
      .finally(() => setLoading(false));
  };

  // printData is the first row of grid (your current logic)
  const printData = (options?.grid ?? [])[0] ?? null;

  return (
    <>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-black-600">{options?.Title}</h3>

        <div className="flex justify-end gap-2">
          {options?.gridtype === "print" ? (
            options?.printVariant === "receipt" ? (
              <ReceiptPrintButton
                filename={options?.Title ?? "export"}
                targetId={
                  options?.printVariant === "receipt"
                    ? "receipt-print"
                    : "print-preview-root"
                }
              >
                {options?.printVariant === "receipt" ? "Print" : "Export"}
              </ReceiptPrintButton>
            ) : (
              <PrintButton data={printData} />
            )
            // <ReceiptPrintButton
            //   filename={options?.Title ?? "export"}
            //   targetId="receipt-print"
            // >
            //   Export
            // </ReceiptPrintButton>
          ) : (
            <button
              onClick={exportData}
              className="bg-primary hover:bg-primary text-white tracking-normal font-semibold text-xs py-2 px-4 rounded-md inline-flex items-center"
            >
              <DownloadIcon width={16} height={16} />
              <h3 className="uppercase ml-2 hidden lg:block">Export</h3>
            </button>
          )}

          <button
            onClick={handleCloseModal}
            className="bg-primary hover:bg-primary text-white tracking-normal font-semibold text-xs py-2 px-4 rounded-md inline-flex items-center"
          >
            <XIcon fill="#ffffff" width={16} height={16} />
            <h3 className="uppercase ml-2 hidden lg:block">Close</h3>
          </button>
        </div>
      </div>

      <div className="mt-2">
        <div className="flex justify-center">
          <div className="w-full">
            {options?.gridtype === "vertical" && (
              <ul className="modal-list">
                {options?.grid.map((record, recordIdx) => (
                  <li className="modal-list-item list-none" key={recordIdx}>
                    <ul className="grid gap-1 my-6 border-b border-gray-200 mb-3">
                      {itemKeys?.map((str, idx) => (
                        <li
                          className={classNames("flex justify-between")}
                          key={idx}
                        >
                          <div
                            className={classNames("py-2 px-3 w-36", {
                              "bg-gray-200": (idx + 1) % 2 !== 0,
                            })}
                          >
                            <span className="text-gray-700 font-semibold text-xs">
                              {str}
                            </span>
                          </div>
                          <div
                            className={classNames(
                              "ml-0.5 py-2 px-3 flex-grow",
                              {
                                "bg-gray-200": (idx + 1) % 2 !== 0,
                              }
                            )}
                          >
                            <span className="text-xs">
                              <TransformValue value={record[str]} />
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}

            {options?.gridtype === "horizontal" && (
              <div className="relative sm:rounded-lg">
                <Table
                  searchRef={searchRef}
                  columns={columns}
                  data={valuee}
                  filterByDateRange={filterByDateRange}
                  drillable={drillable}
                  dateFilter={dateRange}
                  accessFilter={accessFilter}
                  noOfPages={options?.pages}
                  records_per_page={options?.records_per_page}
                  targetId={targetid}
                  layoutId={layoutid}
                />
              </div>
            )}

            {options?.gridtype === "print" && (
              <div className="relative">
                {printData &&
                  (options?.printVariant === "receipt" ? (
                    <ReceiptPrint data={printData} />
                  ) : (
                    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                      <PrintPreview data={printData} />
                    </div>
                  ))}
                {/* <ReceiptPrint data={printData} /> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
